import i18next from 'i18next';
import { get, isEmpty, flatten, pick, uniqueId, keyBy } from 'lodash/fp';
import { ErrorLevel, ERROR_CATEGORY } from 'app/lib/constants';
import { ShareType } from 'app/lib/types/server';
import { isErrorInReference, unwrapErrors, translateError } from 'app/lib/error';
import {
    hasDifferentSchema,
    hasStaleData,
    extractViewId,
    resolveErrorType,
    resolveErrorCategory
} from 'app/store/utils';
import { addWordBreaks } from 'app/lib/utils';
import { TRANSFORMATION_OUT_OF_SYNC } from 'app/store/__generated__/errors';
import { MODEL_DTO_TYPE } from 'app/store/__generated__/types';

export function decorateError(entitiesById, entity, errors) {
    const operationsErrors =
        errors && entity.operationUids
            ? entity.operationUids
                  .map((uuid) => errors[uuid] && { ...errors[uuid], operationUuid: uuid })
                  .filter((err) => err)
                  .map(({ operationUuid, ...err }) => ({
                      ...err,
                      errors: err.errors?.map((error) => ({ ...error, operationUuid }))
                  }))
            : [];
    const viewError = operationsErrors[0] || errors?.[entity.uid];
    let error;
    if (viewError) {
        const originsInError = viewError.origins
            .map((originUid) => ({ uid: originUid, ...errors[originUid] }))
            .filter(({ uid }) => extractViewId(uid) !== entity.uid);

        const originViews = originsInError
            .map(({ uid }) => entitiesById[`${entity.spaceUid}.${extractViewId(uid)}`])
            .filter((view) => !isEmpty(view));

        const errorInfo = {
            level: ErrorLevel.ERROR,
            isReferenceError: isErrorInReference(viewError),
            originsInError,
            originViews,
            operationIndex: viewError.index
        };
        error = { ...viewError, ...errorInfo };
        return error;
    }
}
export function decorateViewEntity(
    { deployedFrom, ...entity },
    usersById,
    entitiesById,
    errors,
    warnings = []
) {
    const spaceOwner = usersById[entity?.spaceOwner] ?? { username: entity?.spaceOwner };
    const isSpaceReadable = entity?.spaceRef?.isReadable;
    const sources = entity.sources;

    let hasSchemaChanged = false;
    let hasDataChanged = false;
    let deployedFromView = null;

    if (deployedFrom && entitiesById?.[deployedFrom]) {
        const fromEntity = entitiesById[deployedFrom];

        deployedFromView = {
            ...fromEntity,
            viewType: fromEntity?._type,
            error: get(fromEntity.uid, errors)
        };

        hasSchemaChanged = hasDifferentSchema(entity);
        hasDataChanged = hasStaleData(entity, deployedFromView);
    }

    const result = {
        ...entity,
        error: decorateError(entitiesById, entity, errors),
        sources,
        hasSchemaChanged,
        hasDataChanged,
        spaceOwner,
        isSpaceReadable
    };

    if (entity._type === ShareType.EMAIL || entity._type === ShareType.GOOGLE_SHEETS) {
        const transformationId = entity.transformationId;
        const parent = entitiesById[transformationId];
        result.operationUids = parent.operationUids;
    }

    if (deployedFromView) {
        result.deployedFrom = deployedFromView;
    }

    if (result._type === MODEL_DTO_TYPE) {
        const isOutOfSync = warnings[entity.uid]?.some(
            (warning) => warning._type === TRANSFORMATION_OUT_OF_SYNC
        );

        result.isOutOfSync = Boolean(isOutOfSync);
    }

    return result;
}

const hasOriginErrors = (error) =>
    error.hasOwnProperty('originViews') &&
    error.hasOwnProperty('originsInError') &&
    error.originViews.length === error.originsInError.length;

export const getViewErrors = (error) => {
    if (isEmpty(error)) {
        return [];
    }

    if (isErrorInReference(error) && hasOriginErrors(error)) {
        const originViewsById = keyBy('uid', error.originViews);
        return error.originsInError.map((err) => {
            const originView = originViewsById[err.uid];
            return {
                id: uniqueId('s'),
                level: error.level,
                type: 'ERROR_IN_REFERENCE',
                category: ERROR_CATEGORY.compilation,
                values: {
                    message: addWordBreaks(err.message),
                    space: originView?.spaceSlug,
                    view: err.uid
                }
            };
        });
    }

    const errors = flatten([error, ...(error.originsInError ?? [])].map(unwrapErrors)).filter(
        (err) => !isErrorInReference(err)
    );

    const originViews = error.originViews?.map((view) => pick(['name', 'spaceSlug', 'uid'], view));

    return errors.map((err) => ({
        id: uniqueId('s'),
        level: ErrorLevel.ERROR,
        type: resolveErrorType(err._type),
        category: resolveErrorCategory(err._type),
        values: { message: translateError(i18next.t, err), ...err },
        originViews: originViews
    }));
};

export const getViewWarnings = (warnings) => {
    if (isEmpty(warnings)) {
        return [];
    }

    return warnings.map((warning) => ({
        id: uniqueId('s'),
        level: ErrorLevel.WARNING,
        type: warning._type,
        category: resolveErrorCategory(warning._type),
        values: warning
    }));
};
